import type {
  CheckpointSettings,
  CurrencyValue,
  FieldType,
  FieldTypeOptions,
  PhoneValue,
  RecordQuery,
  RelationSettings,
} from '@ofmos/shared';
import type {
  AnnotationUser,
  CommentSummary,
  CreateCommentRequest,
  CreateTaskRequest,
  MyTasksPage,
  NoteSummary,
  TaskKind,
  TaskSummary,
  UpdateTaskRequest,
} from '@ofmos/shared/annotations';
import type {
  ApiKeySummary,
  AuthenticatedUser,
  CreatedApiKey,
  CreatedInvitation,
  InvitationPreview,
  InvitationSummary,
  PasswordResetRequestResult,
  RecoveryMode,
  ResetLinkResult,
  ResetPasswordResult,
  UserAccount,
  UserPreferences,
} from '@ofmos/shared/auth';
import type {
  CsvDelimiter,
  ExportRunStatus,
  ImportMatchKeySource,
  ImportMode,
  ImportRunKind,
  ImportRunStatus,
  ImportValueFix,
} from '@ofmos/shared/csv';
import type { FeedsStatus, TouchFeedRetry } from '@ofmos/shared/feeds';
import type { ColumnFormat } from '@ofmos/shared/field-types';
import type { RecordFileItem } from '@ofmos/shared/files';
import type { FilterAnchor } from '@ofmos/shared/filter-ast';
import type { ReadyResponse } from '@ofmos/shared/health';
import type { HealthConfig } from '@ofmos/shared/health-config';
import type { HealthHistoryQuery, HealthHistoryResponse } from '@ofmos/shared/health-history';
import type { IdentityNamespaceKind } from '@ofmos/shared/identity';
import type { MetadataOrigin } from '@ofmos/shared/ids';
import type { InstanceInfoResponse } from '@ofmos/shared/instance';
import type {
  IntakeDryRunResult,
  IntakeMapping,
  IntakeMappingSuggestionResult,
  IntakePullConfig,
  IntakeSource,
  IntakeSourceKeyMinted,
  IntakeSourceList,
  IntakeSourcePatchInput,
  IntakeSourceStatusReport,
  IntakeSourceWriteInput,
  IntakeSubmissionBinding,
  IntakeValidationResult,
} from '@ofmos/shared/intake';
import type { IntakeReviewed } from '@ofmos/shared/intake-reviewed';
import type { ObjectColor } from '@ofmos/shared/object-color';
import type { GroupKey, RecordGroupQuery, RecordGroupTotals } from '@ofmos/shared/record-group';
import type { RecordEventAnnotation } from '@ofmos/shared/record-write';
import type { ReplyHandled } from '@ofmos/shared/reply-handled';
import type { Report, ReportPatchInput, ReportRange, ReportWriteInput } from '@ofmos/shared/report';
import type { CreateRoleRequest, Role, RoleHolder, UpdateRoleRequest } from '@ofmos/shared/roles';
import type { SavedView, SavedViewPatchInput, SavedViewWriteInput } from '@ofmos/shared/view';
import type {
  CreateWebhookSubscriptionRequest,
  UpdateWebhookSubscriptionRequest,
  WebhookDeliveryStatus,
  WebhookDeliverySummary,
  WebhookReplayMode,
  WebhookSubscriptionHealth,
  WebhookSubscriptionSummary,
  WebhookTestResult,
} from '@ofmos/shared/webhook';
import type {
  CreateWorkflowInput,
  PatchWorkflowInput,
  WorkflowDetail,
  WorkflowDraftInput,
  WorkflowRunDetail,
  WorkflowRunSegment,
  WorkflowRunView,
  WorkflowScheduleFireView,
  WorkflowSettings,
  WorkflowSummary,
} from '@ofmos/shared/workflow';
import type { WorkspaceInfoResponse } from '@ofmos/shared/workspace';
import type { ObjectIcon, TagTone } from '../ui';

/**
 * THE CLIENT CONTRACT.
 *
 * Every screen in this app talks to these two interfaces and to nothing else.
 * The fixture implementation (`fixtures/FixtureClient.ts`) and the future HTTP
 * implementation are interchangeable, so wiring the real API is implementing
 * one interface - not rebuilding screens.
 *
 * Mapping to the server (Phase 2+), for the integration track:
 *   RecordClient.query        -> POST /api/query   (RecordQuery from shared, verbatim)
 *   RecordClient.getRecord    -> POST /api/query   (filter on id, limit 1)
 *   RecordClient.create/update-> record layer write path (INV-1), returns WriteResult
 *   RecordClient.listEvents   -> GET  /api/records/:objectId/:recordId/events (INV-4, keyset)
 *   RecordClient.search       -> GET  /api/search   (Postgres full text, search_vector)
 *   MetadataClient.*          -> REST admin ops on meta.object_metadata / field_metadata
 *   AnnotationClient.*        -> REST on /api/records/:objectId/:recordId/{notes,tasks,
 *                                comments}, /api/{notes,tasks,comments}/:id and /api/tasks
 *
 * Types here MIRROR the server's metadata rows. Where `shared` already owns a
 * type (FieldType, RelationSettings, RecordQuery, value shapes) it is imported,
 * never restated.
 */

// --- metadata ---------------------------------------------------------------

/**
 * A SELECT / MULTI_SELECT option as the UI knows it. The `id` is what a record
 * STORES (it is the PG enum label on the server, see field-types.md decision 1);
 * `label` and `color` are presentation and may be edited without touching data.
 * That is why the option editor must never mint a new id for a rename.
 */
export interface SelectOptionDef {
  readonly id: string;
  readonly label: string;
  readonly color?: TagTone;
  readonly position: number;
}

/**
 * Per-field configuration in UI shape. A superset of the storage-facing
 * `FieldTypeOptions` from shared: same relation/currency/phone settings, but the
 * select options carry their labels and colours.
 */
export interface FieldOptions {
  readonly selectOptions?: readonly SelectOptionDef[];
  readonly relation?: RelationSettings;
  readonly defaultCurrencyCode?: string;
  readonly phoneDefaultRegion?: string;
  readonly isUnique?: boolean;
  /**
   * admin-area.md D12: this field is the object's RECORD TITLE. Step 1 of the shared
   * rule in `@ofmos/shared/label-field`, and the only step an admin can reach - the
   * other three are conventions the rule falls back to.
   *
   * TEXT and non-system only, matching the rule's own constraint. Two flagged fields
   * is a state the rule resolves arbitrarily, so `setLabelField` clears the previous
   * holder rather than letting the UI produce one.
   */
  readonly isLabel?: boolean;
  /**
   * identity-resolution.md 1.2: this field's values become matching KEYS in the
   * named namespace. A namespace UUID, never its key - the key is what decides
   * whether two values are comparable at all, and INV-7 forbids keying anything on
   * a mutable name.
   */
  readonly identity?: { readonly namespaceId: string };
  /**
   * file-attachments.md D10, D21: how many attachments one FILES cell may hold. Absent
   * means `FILES_FIELD_DEFAULT_MAX`, which is why the settings panel shows a number rather
   * than a required field - a Files field with no settings is a valid Files field, unlike a
   * SELECT with no options.
   */
  readonly maxFiles?: number;
  /**
   * inbound-intake.md D18: this TEXT field holds paragraphs. `TextDisplay` keeps the line
   * breaks where the record page shows a value and clamps to one line in a table row;
   * `TextInput` becomes a textarea. TEXT only - the server refuses it on every other type.
   */
  readonly multiline?: boolean;
  /**
   * phase-b-coach.md D1: this DATE carries a deadline, counted from a sibling date and an
   * offset in calendar days. `shared/checklist.ts` computes it - here and in the engine, from
   * the same function - and the record page renders the section it belongs to as a checklist.
   */
  readonly checkpoint?: CheckpointSettings;
  /**
   * phase-b-coach.md D2: the system writes this field and a person does not. Rendered
   * read-only with the reason, because a MANUAL write is refused by the record layer - a
   * human-locked computed field is one its own engine could never write again (INV-5).
   */
  readonly computed?: boolean;
  /**
   * DESIGN.md G2 (BR-RECORD): this field is one of the object's key facts, at this place in
   * the record header (1 to 6). `app/record/RecordIdentity.tsx` reads it; nothing else does.
   */
  readonly keyFact?: number;
}

/**
 * WHICH ENTITIES A METADATA READ MAY SEE (admin-area.md D8, and open risk 4).
 *
 * `visible` is what the whole product asks for and what it always got: hidden
 * entities and archived ones are dropped at the mapping boundary. That is not a
 * cosmetic filter - it is also what keeps INV-9 junction objects out of everybody's
 * navigation, out of the record screens and out of the relation pickers.
 *
 * `all` is for the ADMIN SCREENS ONLY. It carries hidden and archived rows through,
 * flagged (`isHidden`, `isArchived`) rather than silently present, so the admin list
 * can offer the restore and the promotion. It MUST NOT become the default anywhere:
 * a leak puts every junction table in every sidebar, which is the exact failure D10
 * names.
 */
export type MetadataVisibility = 'visible' | 'all';

/**
 * The bridge to the shared registry. Shared's `SelectOption` carries
 * id+label+color since WP3; array order is the display order, so the UI's
 * explicit `position` collapses into a sort here.
 */
export function toStorageOptions(options: FieldOptions | undefined): FieldTypeOptions | undefined {
  if (options === undefined) {
    return undefined;
  }
  const selectOptions = options.selectOptions
    ?.slice()
    .sort((a, b) => a.position - b.position)
    .map((option) => ({
      id: option.id,
      label: option.label,
      ...(option.color !== undefined ? { color: option.color } : {}),
    }));
  return {
    ...(selectOptions !== undefined ? { selectOptions } : {}),
    ...(options.relation !== undefined ? { relation: options.relation } : {}),
    ...(options.defaultCurrencyCode !== undefined
      ? { defaultCurrencyCode: options.defaultCurrencyCode }
      : {}),
    ...(options.phoneDefaultRegion !== undefined
      ? { phoneDefaultRegion: options.phoneDefaultRegion }
      : {}),
    ...(options.isUnique !== undefined ? { isUnique: options.isUnique } : {}),
    ...(options.isLabel !== undefined ? { isLabel: options.isLabel } : {}),
    ...(options.identity !== undefined ? { identity: options.identity } : {}),
  };
}

export interface FieldMetadata {
  readonly id: string;
  readonly universalId: string;
  readonly origin: MetadataOrigin;
  readonly objectId: string;
  /** API name (`^[a-z][a-z0-9_]{0,62}$`), also the physical column name. */
  readonly name: string;
  readonly label: string;
  readonly type: FieldType;
  readonly description?: string;
  readonly isSystem: boolean;
  readonly isRequired: boolean;
  readonly position: number;
  /**
   * record-page-and-views.md D8: the record page section this field renders in.
   * `undefined` is "ungrouped" - a trailing section with no heading (D9), never a
   * hidden field.
   */
  readonly groupLabel?: string | undefined;
  /**
   * D8. Optional because only a query that ASKED for hidden entities can ever see
   * one: under the `visible` mode a hidden field never reaches a screen, so every
   * consumer that predates the mode keeps reading exactly what it read before.
   */
  readonly isHidden?: boolean;
  /** D9: soft-deleted (`deleted_at`). Restorable, and excluded from unique indexes. */
  readonly isArchived?: boolean;
  readonly options?: FieldOptions;
}

export interface ObjectMetadata {
  readonly id: string;
  readonly universalId: string;
  readonly origin: MetadataOrigin;
  readonly name: string;
  readonly labelSingular: string;
  readonly labelPlural: string;
  readonly description?: string;
  readonly icon: ObjectIcon;
  readonly isSystem: boolean;
  /** D7: display order in the object list. `float8`; see `MetadataClient.updateObject`. */
  readonly position?: number;
  /** D8; see `FieldMetadata.isHidden`. */
  readonly isHidden?: boolean;
  /** D9: soft-deleted. Its table and its records are untouched. */
  readonly isArchived?: boolean;
  /**
   * INV-9: a junction object the engine minted for a MANY_TO_MANY field. Hidden by
   * default and PROMOTABLE - it is a real object that can carry its own fields
   * ("role of this person in this agency"), which is what D10 exists to expose.
   */
  readonly isJunction?: boolean;
  /**
   * INV-9 again, and the half `isJunction` cannot answer: WHICH MANY_TO_MANY field
   * minted this junction and which two objects it joins. The related-section panel
   * needs all three to decide whether this record sits on the source or the target
   * side of a membership (D3).
   */
  readonly junction?: {
    readonly relationFieldId: string;
    readonly sourceObjectId: string;
    readonly targetObjectId: string;
  };
  /**
   * record-page-and-views.md D11: the sidebar section this object renders under.
   * `undefined` renders in a trailing unlabelled section. It is NOT a visibility
   * flag - hiding is `isHidden`, and conflating the two would add a fourth way for
   * something to fail to appear.
   */
  readonly navGroup?: string | undefined;
  /**
   * ui-foundation.md E9: the object's sidebar tile colour, or `null` for the neutral
   * graphite tile. Like `navGroup` it is PRESENTATION and never visibility.
   */
  readonly color: ObjectColor | null;
  /** Field whose value titles a record in lists, chips and the command menu. */
  readonly labelIdentifierFieldId: string;
}

/**
 * ONE RELATED SECTION'S METADATA (D2), as `GET .../inbound-relations` answers it.
 *
 * The FIELD is the child's own MANY_TO_ONE column, which is what a related list
 * filters on (D1) - never a reverse field, which the platform refuses to make
 * readable. The ORDER of the array is the order the sections render in.
 */
export interface InboundRelation {
  readonly field: FieldMetadata;
  readonly object: ObjectMetadata;
}

export interface CreateObjectInput {
  readonly name: string;
  readonly labelSingular: string;
  readonly labelPlural: string;
  readonly description?: string;
  readonly icon: ObjectIcon;
  /** E9: optional on create - an object made without one draws the graphite tile. */
  readonly color?: ObjectColor | null;
}

/**
 * `labelIdentifierFieldId` USED TO BE HERE AND WAS A LIE (admin-area.md D12). The
 * server stores no such column - it resolves the title field with the shared rule -
 * so a request carrying it was accepted and did nothing, and the "Title field" picker
 * reported success for a change that never happened. The real control is
 * `setLabelField`, which writes `settings.isLabel` on the field itself.
 */
export type UpdateObjectInput = Partial<Omit<CreateObjectInput, 'name'>> & {
  readonly name?: string;
  /**
   * D7 display order. `float8`, so moving an object between two neighbours is the
   * midpoint of their positions and ONE row update, never a renumber. An attribute,
   * never an identifier (INV-7).
   */
  readonly position?: number;
  /** D8 visibility. See `MetadataVisibility` for what hiding actually costs. */
  readonly isHidden?: boolean;
};

export interface CreateFieldInput {
  readonly objectId: string;
  readonly name: string;
  readonly label: string;
  readonly type: FieldType;
  readonly description?: string;
  readonly isRequired?: boolean;
  readonly options?: FieldOptions;
}

/** The TYPE is deliberately absent: changing it is a migration, not an edit (D11). */
export type UpdateFieldInput = Partial<Omit<CreateFieldInput, 'objectId' | 'type'>> & {
  /** D7; see `UpdateObjectInput.position`. */
  readonly position?: number;
  /** D8; see `UpdateObjectInput.isHidden`. */
  readonly isHidden?: boolean;
};

// --- the migration plan, as the preview dialog needs it ----------------------

/** Enough of an object to name it in a sentence. Never an id (see `planSentences.ts`). */
export interface NamedPlanObject {
  readonly id: string;
  readonly name: string;
  readonly label: string;
  readonly labelPlural: string;
  readonly tableName: string;
}

export interface NamedPlanField {
  readonly id: string;
  readonly objectId: string;
  readonly name: string;
  readonly label: string;
  readonly type: string;
}

/**
 * THE ENGINE'S ACTION VOCABULARY, as the JSON arrives (19 kinds, mirroring
 * `packages/server/src/metadata/actions.ts`).
 *
 * A faithful SUBSET: every member carries the fields a sentence needs and drops the
 * ones only an executor uses (table names, enum type names, column suffixes). The
 * union lives here rather than in `wire.ts` because it is the shape the whole preview
 * flow speaks - the client returns it, the hook caches it and `planSentence` switches
 * over it with an `assertNever`, which is what makes a twentieth kind a build error
 * instead of a blank line in a destructive dialog (INV-12).
 */
export type MigrationPlanAction =
  | { readonly kind: 'CREATE_OBJECT'; readonly object: NamedPlanObject }
  | {
      readonly kind: 'RENAME_OBJECT';
      readonly objectId: string;
      readonly fromName: string;
      readonly toName: string;
    }
  | {
      readonly kind: 'UPDATE_OBJECT';
      readonly objectId: string;
      readonly changes: Readonly<Record<string, unknown>>;
    }
  | { readonly kind: 'SOFT_DELETE_OBJECT'; readonly objectId: string }
  | { readonly kind: 'RESTORE_OBJECT'; readonly objectId: string }
  | { readonly kind: 'DROP_OBJECT'; readonly objectId: string; readonly tableName: string }
  | { readonly kind: 'CREATE_FIELD'; readonly field: NamedPlanField }
  | {
      readonly kind: 'RENAME_FIELD';
      readonly fieldId: string;
      readonly fromName: string;
      readonly toName: string;
    }
  | {
      readonly kind: 'UPDATE_FIELD';
      readonly fieldId: string;
      readonly changes: Readonly<Record<string, unknown>>;
    }
  | { readonly kind: 'SOFT_DELETE_FIELD'; readonly fieldId: string }
  | { readonly kind: 'RESTORE_FIELD'; readonly fieldId: string }
  | {
      readonly kind: 'DROP_FIELD';
      readonly fieldId: string;
      readonly objectId: string;
      readonly columnNames: readonly string[];
    }
  | { readonly kind: 'ADD_SELECT_OPTION'; readonly fieldId: string; readonly value: string }
  | {
      readonly kind: 'REMOVE_SELECT_OPTION';
      readonly fieldId: string;
      readonly objectId: string;
      readonly removedValues: readonly string[];
    }
  | {
      readonly kind: 'CHANGE_FIELD_TYPE';
      readonly fieldId: string;
      readonly fromType: string;
      readonly toType: string;
    }
  | {
      readonly kind: 'CREATE_INDEX';
      readonly objectId: string;
      readonly fieldId?: string;
      readonly unique: boolean;
      readonly reason: string;
    }
  | { readonly kind: 'VALIDATE_CONSTRAINT'; readonly objectId: string }
  | { readonly kind: 'BACKFILL_IDENTITY_KEYS'; readonly objectId: string; readonly fieldId: string }
  | { readonly kind: 'DROP_IDENTITY_KEYS'; readonly objectId: string; readonly fieldId: string };

export type MigrationPlanActionKind = MigrationPlanAction['kind'];

/**
 * One destructive step with its COST, computed by the server just now.
 *
 * `detail` is rendered VERBATIM by the dialog. The server appends the names of the
 * webhook subscriptions a DROP_FIELD or a CHANGE_FIELD_TYPE will break to exactly this
 * string, and summarising it away is how "the webhooks quietly stopped last Tuesday"
 * happens (admin-area.md 3.2).
 */
export interface PreflightRow {
  readonly actionKind: string;
  readonly target: string;
  readonly affectedRows: number;
  readonly detail: string;
}

/**
 * What `POST /api/metadata/dry-run` answers: the plan and its cost, applied to nothing.
 *
 * D5's dry-run-first flow. The confirm token is fetched DELIBERATELY, before the dialog
 * opens, instead of being scraped off a 409 the client provoked by calling the
 * destructive endpoint hopefully.
 */
export interface MigrationPreview {
  readonly phaseA: readonly MigrationPlanAction[];
  readonly phaseB: readonly MigrationPlanAction[];
  readonly destructive: readonly MigrationPlanAction[];
  /** Null when the plan destroys nothing; there is then nothing to confirm. */
  readonly confirmToken: string | null;
  /** In PLAN ORDER. `expectedAffectedRows` is this list's counts, in this order (D6). */
  readonly preflight: readonly PreflightRow[];
}

/** Which drop a preview describes. Both go through the same dialog. */
export type DropTarget =
  | { readonly kind: 'object'; readonly objectId: string }
  | { readonly kind: 'field'; readonly fieldId: string };

export interface DestructiveApplyOptions {
  /**
   * D5: the confirm token of the plan the dialog RENDERED, from the dry run. Distinct
   * from the `confirmToken` parameter beside it, which is the api name a human typed
   * and never leaves the browser. Omitted, the client falls back to provoking the
   * server's refusal and reading the token off it.
   */
  readonly planToken?: string;
  /**
   * D6: the `affectedRows` the dialog DISPLAYED, in preflight order. The server
   * re-computes the preflight at apply and refuses with `preflight_drift` when any
   * count GREW - the number that makes the dialog safe is the one number the confirm
   * token does not bind, because the token digests `plan.destructive` only.
   *
   * A count that SHRANK passes: somebody deleted rows in the meantime and the user's
   * consent still covers the smaller destruction.
   */
  readonly expectedAffectedRows?: readonly number[];
}

export interface MetadataClient {
  /**
   * Drops whatever the implementation caches client-side. Called when the live stream
   * reports a new metadata version, which is the only way the model changes under us.
   * The fixture has nothing to drop and implements it as a no-op.
   */
  refresh(): void;

  /** `visibility` defaults to `visible`: the admin screens are the only callers that opt in. */
  listObjects(visibility?: MetadataVisibility): Promise<readonly ObjectMetadata[]>;
  getObject(objectId: string, visibility?: MetadataVisibility): Promise<ObjectMetadata>;
  createObject(input: CreateObjectInput): Promise<ObjectMetadata>;
  updateObject(objectId: string, input: UpdateObjectInput): Promise<ObjectMetadata>;
  /**
   * Destructive (INV-8): the server refuses without a confirm token, and the UI
   * must therefore be able to send one. D9 puts this BEHIND archiving - the dialog
   * is reachable only from the archived list.
   */
  deleteObject(
    objectId: string,
    confirmToken: string,
    options?: DestructiveApplyOptions,
  ): Promise<void>;

  listFields(objectId: string, visibility?: MetadataVisibility): Promise<readonly FieldMetadata[]>;
  createField(input: CreateFieldInput): Promise<FieldMetadata>;
  updateField(fieldId: string, input: UpdateFieldInput): Promise<FieldMetadata>;
  deleteField(
    fieldId: string,
    confirmToken: string,
    options?: DestructiveApplyOptions,
  ): Promise<void>;

  /** D9. Reversible, and therefore the easy action in both lists. */
  archiveObject(objectId: string): Promise<void>;
  restoreObject(objectId: string): Promise<void>;
  archiveField(fieldId: string): Promise<void>;
  restoreField(fieldId: string): Promise<void>;

  /**
   * D5. The plan and its preflight, applied to nothing. Fetched BEFORE the dialog
   * opens, which is what makes the token proof that the caller was shown THIS plan.
   */
  previewDrop(target: DropTarget): Promise<MigrationPreview>;

  /**
   * D12. `fieldId` must be a non-system TEXT field of this object, or null to fall
   * back to the rule's conventions. The previous holder's flag is cleared first, so
   * the in-between state has ZERO flagged fields rather than two.
   */
  setLabelField(objectId: string, fieldId: string | null): Promise<void>;

  /**
   * D2. Which related-record sections this object's record page shows, answered by
   * the server rather than reconstructed in the browser.
   *
   * The browser CANNOT compute this: `listObjects` returns objects without fields,
   * `listFields` is per-object, and the mapping layer drops `isSystem` fields
   * unconditionally - so a client-side derivation would have to walk every field of
   * every object and rebuild the reverse edges the metadata cache already indexes.
   */
  inboundRelations(objectId: string): Promise<readonly InboundRelation[]>;

  /**
   * WHAT MAY ANCHOR A RELATED FILTER on this object
   * (`docs/decisions/relation-filters.md` D27).
   *
   * A DISTINCT read from `inboundRelations`, and the difference is the whole reason it
   * exists: that one is the RECORD PAGE's, and it drops hidden objects - which is right
   * for a section list and fatal for a filter, because an N:N is only reachable through
   * its junction's endpoint fields and junctions are minted hidden. This read keeps a
   * hidden JUNCTION and only a hidden junction, and it also carries the object's OWN
   * MANY_TO_ONE fields, which the inbound set never could.
   *
   * The RULE lives on the server, in the one function the planner and the view validator
   * also call, so a filter the picker offers is one the planner accepts.
   */
  filterAnchors(objectId: string): Promise<readonly FilterAnchor[]>;
}

// --- records ----------------------------------------------------------------

/** Every value a field of any of the THIRTEEN types can hold. */
export type FieldValue =
  | string
  | number
  | boolean
  | PhoneValue
  | CurrencyValue
  | readonly string[]
  /**
   * FILES (`file-attachments.md` D10): the STORED shape, four keys in a fixed order, which
   * the server writes over whatever a client sent. `readonly string[]` above is
   * MULTI_SELECT's option ids and this is deliberately not folded into it - a list of ids
   * and a list of attachments compare, render and cap differently.
   */
  | readonly RecordFileItem[]
  | null;

/** INV-3 ACTOR STAMPS. */
export const ACTOR_SOURCES = ['MANUAL', 'API', 'IMPORT', 'WORKFLOW', 'AGENT', 'SYSTEM'] as const;
export type ActorSource = (typeof ACTOR_SOURCES)[number];

export interface Actor {
  readonly source: ActorSource;
  readonly userId?: string;
  /** Resolved for display by the server; ids alone are unreadable in a timeline. */
  readonly displayName?: string;
  readonly context?: string;
}

/** INV-5 HUMAN PRECEDENCE, as the UI needs it: which fields a human owns. */
export interface FieldProvenance {
  readonly userId: string;
  readonly displayName?: string;
  readonly at: string;
}

export interface RecordRow {
  readonly id: string;
  /** Keyed by field UUID, never by name (INV-7). */
  readonly values: Readonly<Record<string, FieldValue>>;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdBy: Actor;
  readonly updatedBy: Actor;
  /** Keyed by field UUID. Present only for fields a human corrected. */
  readonly provenance: Readonly<Record<string, FieldProvenance>>;
  /**
   * ARCHIVED (admin-area.md D21). Set = the record is soft-deleted: it still exists, it
   * is read-only, and restoring it is one call.
   *
   * It rides on the ROW rather than being inferred from "no list returned it", because
   * neither surface that needs it can do that inference. The archived segment asks for
   * tombstones on purpose, and the record page has to say WHY it is read-only - "absent
   * from a list" is not a fact a record page is ever handed.
   */
  readonly deletedAt: string | null;
  /**
   * RELATION LABELS, BY FIELD ID (record-page-and-views.md D3).
   *
   * `POST /api/query` already resolves every relation's target label in ONE statement
   * per relation FIELD - `record-store/labels.ts` says in as many words that the N+1
   * version "is the single most common way a CRM list view becomes slow". The browser
   * then threw the label away and re-fetched it one record at a time through
   * `useRelationLabel`, which on a memberships panel is one HTTP round trip per row.
   *
   * DEVIATION FROM D3, FLAGGED. The document says `toFieldValue` should keep
   * `{id, label}` INSIDE the value. It must not: `values` is what the shared JS
   * evaluator reads (`filterMatch.ts` -> INV-6), so an object where a uuid string is
   * expected would make every RELATION condition - which D20 adds an editor for -
   * evaluate false, and `reconcileFilteredLists` would drop rows the server keeps.
   * The label rides BESIDE the value instead: same boundary (`mapping.ts`), same
   * saving, and one filter semantics.
   *
   * Absent for a single-record read, which returns bare ids; the display layer falls
   * back to `useRelationLabel` exactly as before.
   */
  readonly relationLabels?: Readonly<Record<string, string>>;
}

export interface RecordPage {
  readonly records: readonly RecordRow[];
  readonly nextCursor?: string;
  /**
   * The size of the WHOLE filtered set, not of this page (D20). Present only when the
   * caller asked for it: it is a second, unbounded scan of the same filter
   * (`record-query.ts`), so a related-section header pays for it and a table does not.
   */
  readonly totalCount?: number;
}

/** A field an automated writer was refused on (INV-5). */
export interface BlockedField {
  readonly fieldId: string;
  readonly attempted: FieldValue;
  readonly kept: FieldValue;
  readonly heldBy: FieldProvenance;
}

/**
 * The three honest outcomes of a write. `pending_approval` is INV-10 (agent and
 * gated writes queue instead of applying), `write_blocked` is INV-5. The UI must
 * render all three differently - "saved" for something that is waiting for a
 * human is exactly the lie this model exists to prevent.
 */
export type WriteResult =
  | { readonly status: 'applied'; readonly record: RecordRow }
  | { readonly status: 'pending_approval'; readonly pendingChangeId: string }
  | {
      readonly status: 'write_blocked';
      readonly record: RecordRow;
      readonly blocked: readonly BlockedField[];
    };

/** INV-4 EVENTS FROM THE WRITE PATH: real before/after diffs. */
export interface FieldDiff {
  readonly fieldId: string;
  readonly before: FieldValue;
  readonly after: FieldValue;
}

/**
 * The server's event vocabulary (`RECORD_EVENT_ACTIONS` in shared) plus one kind only a
 * fixture can produce.
 *
 * The fixture-only `note` kind is GONE. notes-tasks-comments.md 11.2 named it as the one
 * naming collision to resolve at implementation, and resolving it in favour of the real
 * vocabulary is the only honest option: `note_added` is a server action carrying an
 * annotation block with the row id and the body diff, while the placeholder meant "a note
 * happened, roughly" and carried a free-text `message`. Keeping both would leave two ways
 * to say one thing, one of which no server can ever emit.
 *
 * `pending_approval` is a fixture-only TIMELINE entry as well, but for a different
 * reason now that INV-10 exists: a proposal is not a record event, it is a row in
 * `meta.pending_change`, and the Pending changes screen is where it is rendered. What
 * DOES reach the timeline is `proposal_expired` - a proposal against this record that
 * nobody decided - and the approval metadata carried on the write an approval released.
 */
export const RECORD_EVENT_TYPES = [
  'created',
  'updated',
  'deleted',
  'restored',
  'erased',
  'lock_released',
  'write_blocked',
  'proposal_expired',
  /**
   * identity-resolution.md 4.5. `merged_into` REPLACES `soft_deleted` on the loser -
   * one operation, one event - so a timeline that showed both would be noise. The
   * loser's own history stays true under its own record id and is not scrubbed: a
   * merge is not an erasure.
   */
  'merged_from',
  'merged_into',
  'unmerged',
  /** The aggregate above the fan-out threshold, on a repointed CHILD (4.4 step 3). */
  'merge_repointed',
  'pending_approval',
  /**
   * notes-tasks-comments.md 3.2 (D3): the eleven annotation actions, named after the
   * server actions rather than collapsed into three kinds. `task_completed` and
   * `task_reopened` in particular must stay distinguishable from `task_edited` all the way
   * into the renderer - completion is the state change a reader scans a timeline for.
   */
  'note_added',
  'note_edited',
  'note_deleted',
  'task_added',
  'task_edited',
  'task_completed',
  'task_reopened',
  'task_deleted',
  'comment_added',
  'comment_edited',
  'comment_deleted',
] as const;
export type RecordEventType = (typeof RECORD_EVENT_TYPES)[number];

export interface RecordEvent {
  readonly id: string;
  readonly objectId: string;
  readonly recordId: string;
  readonly type: RecordEventType;
  readonly at: string;
  readonly actor: Actor;
  readonly diff: readonly FieldDiff[];
  /** Free text for notes and for the reason a write was refused. */
  readonly message?: string;
  /**
   * 3.3's annotation block, carried straight through from the event payload. It is what
   * lets the timeline render a note card - author, body, the row id its comment thread
   * hangs off - without a second request per entry, which is the whole reason the full
   * body rides in the diff instead of a truncated preview.
   */
  readonly annotation?: RecordEventAnnotation;
  /**
   * Why the writer made this write, when it said so (redesign register A2): the payload's
   * `values.reason`, carried through untranslated. A correction made under the "Data
   * correction" key reads, with the actor's `context` and the diff, as "<Field> corrected
   * from <before> to <after> (<reason>), <date>".
   */
  readonly reason?: string;
}

/**
 * One page of a record's timeline. Keyset, newest first (3.3): the endpoint used to return
 * every event of a record, and one note edit can carry two 20k bodies in its diff.
 *
 * `nextCursor` is plumbed through the whole stack even though nothing pages yet, so the
 * "load more" this design owes is a UI change and not a client-contract change.
 */
export interface RecordEventPage {
  readonly events: readonly RecordEvent[];
  readonly nextCursor: string | null;
}

/** One page of timeline, matching the server's own default (`RECORD_EVENT_PAGE_DEFAULT`). */
export const RECORD_EVENT_PAGE_SIZE = 50;

export interface SearchHit {
  readonly objectId: string;
  readonly recordId: string;
  readonly label: string;
  readonly secondary?: string;
}

/**
 * THE ARCHIVED VIEW (admin-area.md D21), as an option rather than as a filter the caller
 * builds.
 *
 * `archivedOnly` is not `RecordQuery.includeDeleted` renamed. `includeDeleted` widens the
 * result to live AND archived; the segment shows archived ONLY, which additionally needs a
 * condition on `deleted_at`. Naming the VIEW here and letting each implementation reach it
 * its own way is what keeps that second half from leaking into every call site.
 *
 * Why the caller cannot just pass the condition itself: `deleted_at` is a SYSTEM field, and
 * `isDisplayableField` drops system fields at the metadata boundary, so no screen ever holds
 * its field id. The implementations do - the HTTP client already resolves system field ids to
 * build a `select` - so the condition is built there, out of the SAME filter AST every other
 * filter uses (INV-6: one AST, one evaluator, no second semantics).
 *
 * The two alternatives, and why they lost:
 *  - FETCH `includeDeleted` AND DROP LIVE ROWS IN THE COMPONENT. The server has no
 *    archived-only mode, so a 50-row page of a mostly-live object comes back with two
 *    tombstones in it and the segment reads as empty. Paging would be a lie.
 *  - EXPOSE `deleted_at` TO THE APP LAYER so a screen can name it. That puts a system field
 *    into every column picker, sort menu and filter builder in the product to serve one
 *    segment.
 */
export interface RecordQueryOptions {
  /** Archived rows INSTEAD OF live ones, never both: that is what the segment shows. */
  readonly archivedOnly?: boolean;
  /**
   * Ask for `RecordPage.totalCount` (D20). Off by default and deliberately explicit:
   * the server answers it with a SECOND scan that no page size bounds, so "Calls (11)"
   * opts in and a fifty-row table does not.
   */
  readonly withTotalCount?: boolean;
}

/**
 * THE ARCHIVED SEGMENT, FOR A GROUPED READ (group-by-aggregation.md D9).
 *
 * `RecordQueryOptions`'s `archivedOnly` half and nothing else: `withTotalCount` has no
 * meaning here, because a grouped read returns a grand total by construction.
 *
 * The segment AGGREGATES, and that is a consequence rather than a feature. `archivedOnly`
 * is compiled into an ordinary AST condition on `deleted_at` plus `includeDeleted`, so an
 * archived table gets its own footer over its own rows with no special case anywhere - and
 * the number under a column stays computed over exactly the rows the column shows, which
 * is the property this whole module rests on.
 */
export interface RecordGroupOptions {
  readonly archivedOnly?: boolean;
}

/**
 * ONE GROUP, AS THE BROWSER HOLDS IT (group-by-aggregation.md D2, D18).
 *
 * `RecordGroup` in shared is this shape with the SERVER's row type. The only difference
 * is the one every other read here has: the seeded rows are `RecordRow`s, with their
 * system fields and their resolved relation labels, so a card or a table row renders out
 * of a group exactly as it renders out of a page.
 */
export interface RecordGroupEntry extends RecordGroupTotals {
  readonly key: GroupKey;
  /** Index-aligned with `key`; non-null only where the server resolved a relation label. */
  readonly labels: readonly (string | null)[];
  /** The seeded preview rows. Empty when `rowsPerGroup` is 0 (D19). */
  readonly records: readonly RecordRow[];
}

/**
 * THE GROUPED READ'S ANSWER: `POST /api/group`, one repeatable-read snapshot behind it.
 *
 * `total` is the grand-total row - the same statement with an empty `groupBy` - which is
 * what makes a footer's parts-and-whole claim checkable, and is the comparand a
 * synthesised empty group must equal operation by operation (D29).
 */
export interface RecordGroupSet {
  readonly groups: readonly RecordGroupEntry[];
  /**
   * More groups exist beyond the cap, and NOT how many: counting the distinct values
   * would be a second full scan, so the sentence on screen is "the first 50 groups" (D20).
   */
  readonly hasMoreGroups: boolean;
  readonly total: RecordGroupTotals;
  /** The discovered codes, ordered, present only when a CURRENCY aggregate was asked for. */
  readonly currencyCodes?: readonly string[] | undefined;
}

export interface GetRecordOptions {
  /**
   * Read the TOMBSTONE rather than 404 on it. It also suppresses the server's merge
   * forward-resolution (`records/read.ts`), which is the point and not a side effect: on a
   * merged-away id this hands back the loser the caller asked for instead of silently
   * answering with the winner's data under the loser's URL.
   */
  readonly includeDeleted?: boolean;
}

export interface JunctionLinkInput {
  /** The MANY_TO_MANY field the membership belongs to, e.g. `agency.people`. */
  readonly relationFieldId: string;
  readonly sourceId: string;
  readonly targetId: string;
  /** The junction's OWN user fields (role, primary contact, joined at). */
  readonly values?: Readonly<Record<string, FieldValue>>;
}

export interface JunctionLinkResult {
  /** The membership row, so the section can render it without a refetch. */
  readonly record: RecordRow;
  readonly junctionObjectId: string;
  /** True when a removed membership came back rather than a new one being created. */
  readonly restored: boolean;
}

/**
 * WHAT AN ERASURE WOULD DESTROY, for one person (accounts F2, `admin-area.md` D23).
 *
 * The unit is the MERGE CLUSTER and never one row (`identity-resolution.md` 5.6): after a
 * merge, erasing the winner leaves the loser's name, address and history in an ordinary row.
 * The cluster is therefore the first thing this type carries and the first thing a screen
 * showing it has to say.
 */
export interface EraseClusterMember {
  readonly recordId: string;
  readonly label: string | null;
  readonly eventCount: number;
  /** False for a member that is a merge tombstone rather than the live record. */
  readonly live: boolean;
}

export interface EraseInboundRelation {
  readonly objectId: string;
  readonly objectName: string;
  readonly fieldId: string;
  readonly fieldName: string;
  readonly count: number;
}

export interface ErasePreflight {
  readonly objectId: string;
  readonly recordId: string;
  readonly cluster: readonly EraseClusterMember[];
  readonly eventCount: number;
  readonly inboundRelations: readonly EraseInboundRelation[];
  readonly pendingChangeCount: number;
  readonly openPendingChangeCount: number;
  readonly mergeJournalCount: number;
  readonly identityKeyCount: number;
  readonly annotationCounts: {
    readonly notes: number;
    readonly tasks: number;
    readonly comments: number;
  };
  readonly attachmentCount: number;
  readonly attachmentBytes: number;
  /** Must be echoed back to `erase`. It digests the SORTED CLUSTER IDS. */
  readonly confirmToken: string;
}

export interface EraseResult {
  readonly recordId: string;
  /** The whole cluster: every id this erasure removed. */
  readonly erasedRecordIds: readonly string[];
  readonly eventsScrubbed: number;
  readonly attachmentsPurged: number;
  readonly attachmentsDeferred: number;
  /** The server's own sentence when the bounded purge left bytes for the hourly job. */
  readonly attachmentsNote: string | null;
}

/** How much one OBJECT holds, in the four numbers its alert names (accounts F2, D-F2.2). */
export interface EraseAllPreflight {
  readonly objectId: string;
  readonly objectName: string;
  /** Every record of the object, live and archived. */
  readonly recordCount: number;
  /** How many merge clusters those records form, which is how many transactions run. */
  readonly clusterCount: number;
  readonly eventCount: number;
  readonly attachmentCount: number;
  readonly confirmToken: string;
}

export interface EraseAllResult {
  readonly objectId: string;
  readonly erasedRecordIds: readonly string[];
  readonly clustersErased: number;
  readonly eventsScrubbed: number;
  readonly attachmentsPurged: number;
  readonly attachmentsDeferred: number;
}

export interface RecordClient {
  /** The RecordQuery contract from shared, unchanged: POST /api/query. */
  query(query: RecordQuery, options?: RecordQueryOptions): Promise<RecordPage>;
  /**
   * THE GROUPED READ (group-by-aggregation.md D21): POST /api/group, the one grouped path.
   *
   * `RecordGroupQuery` COMPOSES a `RecordQuery` rather than extending it, so the filter,
   * the sort and the select are literally the same object this client already sends -
   * which is what makes the number under a column and the rows in it the same population
   * by construction (D1, D3).
   *
   * IT IS NOT A SECOND WAY TO LIST RECORDS. `rowsPerGroup` seeds a PREVIEW into each
   * group; the row statement ranks with `row_number()` and emits no cursor, so expanding a
   * group mounts the ordinary infinite list from page 1 (D19).
   *
   * The FIXTURE implements a bounded subset of this and REFUSES the rest out loud (D28):
   * date buckets and CURRENCY aggregates are the two places real arithmetic hides, and a
   * review build that showed a plausible number for either would be a second grouping
   * implementation nothing pins against the SQL.
   */
  queryGroups(query: RecordGroupQuery, options?: RecordGroupOptions): Promise<RecordGroupSet>;
  getRecord(
    objectId: string,
    recordId: string,
    options?: GetRecordOptions,
  ): Promise<RecordRow | null>;
  createRecord(
    objectId: string,
    values: Readonly<Record<string, FieldValue>>,
  ): Promise<WriteResult>;
  updateRecord(
    objectId: string,
    recordId: string,
    values: Readonly<Record<string, FieldValue>>,
  ): Promise<WriteResult>;
  /** SOFT delete. The row survives with `deletedAt` set; `restoreRecord` is its inverse. */
  deleteRecord(objectId: string, recordId: string): Promise<void>;
  /**
   * The inverse of `deleteRecord`, open to MEMBERS (D21): soft delete is the ordinary
   * reversible delete every member can perform, so its undo cannot need an administrator.
   *
   * REFUSES ON A MERGE LOSER, and the refusal is the server's
   * (`records/write.ts` `flipDeletion`): restoring a loser on its own would produce a
   * record that is live but keyless, whose id still forward-resolves to the winner. The
   * operation that works is undoing the merge, and the message says so.
   */
  restoreRecord(objectId: string, recordId: string): Promise<void>;
  /**
   * ADD AN N:N MEMBERSHIP (D5), through the one door that exists.
   *
   * `createRecord` on a junction object answers 422 and must keep doing so: the two
   * endpoints are SYSTEM fields, and INV-3 says the write path fills those, callers
   * cannot. This route names the RELATION FIELD - the id "Add member" actually has -
   * and the record layer resolves the junction and writes both endpoints itself.
   *
   * Re-adding somebody who was removed RESTORES the tombstone rather than creating a
   * second membership row, and says so in `restored`, because two rows for one
   * membership splits its role history.
   *
   * Removal has no method of its own: it is `deleteRecord` on the membership.
   */
  linkJunction(input: JunctionLinkInput): Promise<JunctionLinkResult>;
  /**
   * D24: HAND ONE FIELD BACK TO AUTOMATED WRITERS, and it is per field by design.
   *
   * `provenance.md:33` describes release as per field, one click, and its cost is the
   * point: releasing four hundred locks from a summary screen is the silent overwrite
   * INV-5 exists to prevent. The route is guarded by a HUMAN check rather than by mere
   * presence - an API key holding `update` could otherwise release a human lock and then
   * overwrite the value it had just unlocked - so this call exists only for a signed-in
   * person and the UI renders it only for one.
   *
   * `changed` is false when the field carried no lock, which is not an error: two people
   * on the same record is a race, not a mistake.
   */
  releaseFieldLock(
    objectId: string,
    recordId: string,
    fieldId: string,
  ): Promise<{ readonly record: RecordRow; readonly changed: boolean }>;
  listEvents(
    objectId: string,
    recordId: string,
    options?: { readonly limit?: number; readonly cursor?: string },
  ): Promise<RecordEventPage>;
  /** Cross-object full-text search for the command menu. */
  search(term: string, limit?: number): Promise<readonly SearchHit[]>;
  /**
   * THE FOUR ERASE CALLS (accounts F2). Session-only on the server, all four, and the two
   * PREFLIGHTS are as gated as the two applies: a preflight says how many notes, comments
   * and attachments one person has, which is the disclosure the erasure is about.
   *
   * `erase` and `erasePreflight` are the GDPR answer for one person and have no screen in
   * this package - the entry point belongs on the live record's menu, which is another
   * package's file. They are on the client because the pair is one contract and a half-built
   * client is how the second half gets built differently.
   */
  erasePreflight(objectId: string, recordId: string): Promise<ErasePreflight>;
  erase(objectId: string, recordId: string, confirmToken: string): Promise<EraseResult>;
  eraseAllPreflight(objectId: string): Promise<EraseAllPreflight>;
  eraseAll(
    objectId: string,
    confirmToken: string,
    expectedRecordCount: number,
  ): Promise<EraseAllResult>;
}

// --- auth -------------------------------------------------------------------

/**
 * The session surface. It is part of the client contract for the same reason the other
 * two are: the screens must not know whether they talk to HTTP or to a fixture, and a
 * test that mounts a guarded route must be able to say who is signed in without a
 * network stub.
 */
export interface AuthClient {
  /** The current session, or `null` when there is none. Never throws on a 401. */
  me(): Promise<AuthenticatedUser | null>;
  login(email: string, password: string): Promise<AuthenticatedUser>;
  logout(): Promise<void>;
  /**
   * Bootstrap only: on an instance with no accounts this creates the admin
   * (`REGISTRATION_MODE=invite`). Afterwards the server refuses it and an admin creates
   * accounts through `createUser`.
   */
  register(input: {
    readonly email: string;
    readonly password: string;
    readonly name: string;
  }): Promise<AuthenticatedUser>;
  /** Admin only. */
  listUsers(): Promise<readonly UserAccount[]>;
  createUser(input: {
    readonly email: string;
    readonly password: string;
    readonly name: string;
    /**
     * roles-and-permissions.md D18.1 rule 1: the legacy scalar is REPLACED, not kept
     * beside this. An empty list means the `Coach` role, which the server resolves - a
     * uuid cannot be a schema default.
     */
    readonly roleIds: readonly string[];
  }): Promise<AuthenticatedUser>;
  /**
   * Activation and display name (admin-area.md D14). NOT email: it is the login and the
   * unique key, so changing it silently reassigns an account. NOT roles either, since
   * D18.1 rule 4 - assignment is `assignUserRoles`, because the escalation ceiling has to
   * see the whole set being assigned rather than one scalar at a time.
   */
  updateUser(
    userId: string,
    changes: {
      readonly isActive?: boolean;
      readonly name?: string;
    },
  ): Promise<UserAccount>;
  /**
   * Generates a password, stores its hash, returns it ONCE and revokes every session
   * of that account (D15). There is no mail path in this installation; the admin hands
   * the string over the way they hand over every other credential here.
   */
  resetPassword(userId: string): Promise<ResetPasswordResult>;
  /** Your own password. Verifies the current one; leaves the caller's own session alive. */
  changeOwnPassword(input: {
    readonly currentPassword: string;
    readonly newPassword: string;
  }): Promise<{ readonly revokedSessions: number }>;

  // --- one-time links (accounts F1) -----------------------------------------

  /**
   * INVITATIONS AND PASSWORD RECOVERY, and the shape they all share: the server answers a
   * PATH and the browser turns it into a link with `window.location.origin`.
   *
   * That is not an inconvenience worked around - it is the only honest source of an origin
   * on a request. Building the link server-side from the `Host` header is host-header
   * injection, and building it from configuration would make a correct link depend on
   * `PUBLIC_BASE_URL` being right on an instance that was never set up to mail anything.
   */
  listInvitations(): Promise<readonly InvitationSummary[]>;
  /** The accept path is in the RESULT and nowhere else: it is shown once, like a password. */
  createInvitation(input: {
    readonly email: string;
    readonly roleIds: readonly string[];
  }): Promise<CreatedInvitation>;
  revokeInvitation(invitationId: string): Promise<void>;
  /** PUBLIC: whoever holds the link has no account yet, so this call carries no session. */
  getInvitation(token: string): Promise<InvitationPreview>;
  /** Creates the account AND signs the person in: the link plus the password is the proof. */
  acceptInvitation(
    token: string,
    input: { readonly name: string; readonly password: string },
  ): Promise<AuthenticatedUser>;
  /**
   * The admin-issued reset link (D-F1.4). It replaces the generated password as the pane's
   * reset action: the person chooses their own, so the admin never learns it.
   */
  createResetLink(userId: string): Promise<ResetLinkResult>;
  /** Whether this instance can mail a link, so the recovery screen says the true thing. */
  getRecoveryMode(): Promise<RecoveryMode>;
  /**
   * PUBLIC: what this installation is called, for the sign-in screen's second line
   * (login.md 2.3). On `AuthClient` beside `getRecoveryMode` because it is the same kind of
   * fact - about the INSTANCE, identical for every visitor, readable with no session - and
   * the credential screens are the only readers either one has.
   */
  getWorkspace(): Promise<WorkspaceInfoResponse>;
  /** Always succeeds, whether or not the address exists: no account-existence oracle. */
  requestPasswordReset(email: string): Promise<PasswordResetRequestResult>;
  checkPasswordReset(token: string): Promise<void>;
  /** Sets the password and ends every session of that account. Does NOT sign anybody in. */
  setPasswordWithToken(token: string, password: string): Promise<void>;

  /**
   * PER-USER PREFERENCES (dashboard.md D33), and the reason `AppClient` is still at NINE
   * surfaces after the dashboard shipped.
   *
   * The resource is the caller's own user row, exactly like the password above, so these
   * are two METHODS on a client that already exists rather than a tenth client - which
   * is also what keeps `FixtureClient` cheap enough for the review build to render the
   * dashboard with no server at all.
   */
  getPreferences(): Promise<UserPreferences>;
  /** Upsert one key. The value replaces whatever was stored under it. */
  setPreference(key: string, value: unknown): Promise<void>;

  /** API keys (D16). `last_used_at` is what makes the screen worth having. */
  listApiKeys(): Promise<readonly ApiKeySummary[]>;
  /** Returns the plaintext key, exactly once. It is never retrievable again. */
  createApiKey(input: {
    readonly label: string;
    /** D18 step 4: an empty list is legal and means a key that can do nothing. */
    readonly roleIds: readonly string[];
    readonly isAgent: boolean;
  }): Promise<CreatedApiKey>;
  /** Soft: `revoked_at` is set and the row stays, so an event naming it stays readable. */
  revokeApiKey(keyId: string): Promise<boolean>;

  // --- access roles (roles-and-permissions.md section 6) --------------------

  /**
   * READABLE BY ANY SIGNED-IN PERSON, which is guardrail 7 (DENIAL IS VISIBLE, NOT
   * HIDDEN): the Members table renders role chips for everybody, and "ask somebody who
   * can" needs an addressee.
   */
  listRoles(): Promise<readonly Role[]>;
  getRole(roleId: string): Promise<Role>;
  /** Who holds this role, people and keys together - a key is a principal too (section 6). */
  listRoleHolders(roleId: string): Promise<readonly RoleHolder[]>;
  /** The read-only "who can administer access" view, reachable by anyone. */
  listRoleAdministrators(): Promise<readonly RoleHolder[]>;
  createRole(input: CreateRoleRequest): Promise<Role>;
  updateRole(roleId: string, patch: UpdateRoleRequest): Promise<Role>;
  deleteRole(roleId: string): Promise<void>;
  /** The WHOLE set, because guardrail 1 asks about the union rather than about one role. */
  assignUserRoles(userId: string, roleIds: readonly string[]): Promise<void>;
  assignApiKeyRoles(apiKeyId: string, roleIds: readonly string[]): Promise<void>;
}

// --- the operator surface (admin-area.md section 5, D24) --------------------

/** One object's agent write policy, as `/settings/agent` renders it (D20). */
export interface AgentObjectPolicy {
  readonly objectId: string;
  /** What is STORED. */
  readonly directWrite: boolean;
  /**
   * What will actually HAPPEN. The kill switch overrides every opt-in, and an operator
   * reading `directWrite: true` while the brake is on would draw exactly the wrong
   * conclusion.
   */
  readonly effectiveDirectWrite: boolean;
  readonly updatedAt: string;
  readonly updatedBy: string | null;
}

export interface AgentPolicySnapshot {
  /** `env OR db` (D17): what the gating path will do right now. */
  readonly forcePending: boolean;
  /**
   * The environment variable. It can only force the switch ON, so when this is true the
   * UI must render the control as locked rather than as a switch that appears to work.
   */
  readonly forcePendingEnvFloor: boolean;
  readonly forcePendingUpdatedAt: string | null;
  readonly forcePendingUpdatedBy: string | null;
  /** D19: refuses notes, tasks and comments only. Record writes are untouched. */
  readonly agentAnnotationsEnabled: boolean;
  readonly agentAnnotationsUpdatedAt: string | null;
  readonly agentAnnotationsUpdatedBy: string | null;
  readonly pendingTtlDays: number;
  readonly objects: readonly AgentObjectPolicy[];
}

/**
 * The operator's surface: the switches on `/settings/agent` and the panel on
 * `/settings/instance`. Its own interface rather than more methods on `AuthClient`,
 * because none of it is about who you are.
 */
export interface AdminClient {
  getAgentPolicy(): Promise<AgentPolicySnapshot>;
  /** Both global switches (D17, D19). Send the one that moved. */
  setGlobalAgentPolicy(changes: {
    readonly agentWritesForcePending?: boolean;
    readonly agentAnnotationsEnabled?: boolean;
  }): Promise<AgentPolicySnapshot>;
  /** The per-object direct-write opt-in (D20). */
  setObjectAgentPolicy(objectId: string, directWrite: boolean): Promise<AgentObjectPolicy>;
  /** The Version card (D24). Admin-only, and deliberately not the open `/health` probe. */
  getInstance(): Promise<InstanceInfoResponse>;
  /** Open phase-B actions: pending, applying, failed. The Background changes card. */
  listMigrationActions(): Promise<readonly MigrationActionSummary[]>;
  /** Operator retry of a FAILED action. `failed` is terminal and needs a human. */
  retryMigrationAction(actionId: string): Promise<void>;
  /** Database and outbox health, from the readiness probe. */
  getReady(): Promise<ReadyResponse>;
}

/** One open phase-B action, as the Background changes card renders it. */
export interface MigrationActionSummary {
  readonly id: string;
  readonly batchId: string;
  readonly actionType: string;
  readonly status: 'pending' | 'applying' | 'failed';
  readonly attempts: number;
  readonly error: string | null;
  readonly objectMetadataId: string | null;
  readonly fieldMetadataId: string | null;
  readonly createdAt: string;
}

// --- pending changes (INV-10) -----------------------------------------------

/**
 * A proposal, as the approval screen needs it. Mirrors `PendingChange` in shared with
 * the UI's own value type, and adds nothing: the screen is a renderer over the table
 * (pending-change.md rule 3), so inventing fields here would mean inventing facts.
 */
export interface PendingChangeSummary {
  readonly id: string;
  readonly objectId: string;
  /** For `merge` this is the WINNER (identity-resolution.md section 3). */
  readonly recordId: string | null;
  readonly kind: 'create' | 'update' | 'delete' | 'merge';
  /**
   * Set exactly when `kind === 'merge'`: the record that would be merged away. It is a
   * COLUMN and not part of `values`, which is why flipping the winner supersedes a
   * proposal rather than editing it (section 3).
   */
  readonly loserRecordId: string | null;
  readonly status: 'pending' | 'approved' | 'edited_approved' | 'rejected' | 'expired';
  readonly values: Readonly<Record<string, FieldValue>>;
  /** Present on edited_approved: what the human changed it to before approving. */
  readonly editedValues: Readonly<Record<string, FieldValue>> | null;
  readonly proposedBy: Actor;
  readonly decidedBy: Actor | null;
  readonly decidedAt: string | null;
  readonly reason: string | null;
  readonly createdAt: string;
  readonly expiresAt: string;
}

/** One proposal plus the record as it stands, which is what makes a diff a diff. */
export interface PendingChangeDetail {
  readonly pendingChange: PendingChangeSummary;
  /** Null for a `create` proposal: there is nothing to compare against yet. */
  readonly current: RecordRow | null;
}

/**
 * The result of a decision. `blocked` is not an afterthought: an approved write can
 * still be refused per field if a human locked it after the proposal was made (INV-5),
 * and a screen that said "approved" without mentioning it would be lying.
 */
export interface ApprovalOutcome {
  readonly pendingChange: PendingChangeSummary;
  readonly record: RecordRow | null;
  readonly blocked: readonly BlockedField[];
}

export interface PendingClient {
  list(filter?: {
    readonly status?: PendingChangeSummary['status'];
    readonly objectId?: string;
  }): Promise<readonly PendingChangeSummary[]>;
  /** The badge count. Its own call: it is rendered on every screen. */
  count(): Promise<number>;
  get(id: string): Promise<PendingChangeDetail | null>;
  /** `values` present = edit-then-approve; it REPLACES the proposal. */
  approve(id: string, values?: Readonly<Record<string, FieldValue>>): Promise<ApprovalOutcome>;
  reject(id: string, reason: string): Promise<PendingChangeSummary>;
}

// --- identity resolution (docs/decisions/identity-resolution.md) -------------

/**
 * A NAMESPACE is the answer to "are these two values comparable at all" (1.3). It is
 * the single most important safety property in the design, which is why `key`, `kind`
 * and `defaultRegion` are FIXED at creation: all three change what matches what,
 * retroactively, for every key already stored.
 */
export interface IdentityNamespace {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly kind: IdentityNamespaceKind;
  /** `phone` only: canonicalises every key in this namespace, not the field's region. */
  readonly defaultRegion: string | null;
  readonly description: string | null;
  /** Keys stored under it. The number an admin needs before deleting anything. */
  readonly keyCount: number;
  /** Live fields declaring this namespace. Deleting refuses while it is above zero (5.2). */
  readonly fieldCount: number;
}

export interface CreateNamespaceInput {
  readonly key: string;
  readonly label: string;
  readonly kind: IdentityNamespaceKind;
  readonly defaultRegion?: string;
  readonly description?: string;
}

/** Only the two mutable columns. The other three would redefine existing keys (1.3). */
export interface UpdateNamespaceInput {
  readonly label: string;
  readonly description?: string;
}

/** One key a record holds, in the shape the identity panel renders. */
export interface IdentityKeyEntry {
  readonly namespaceId: string;
  readonly namespaceKey: string;
  readonly namespaceLabel: string;
  readonly fieldId: string;
  readonly valueNorm: string;
  readonly valueRaw: string;
  /** False = an ALIAS the record used to hold (1.5). Weaker evidence, never a lie. */
  readonly isCurrent: boolean;
}

/**
 * 2.6: the key index is global, so "which records anywhere hold this value" is one
 * indexed query. It is a READ and can never cause a merge, so it carries none of the
 * risk - which is what lets it cross object boundaries when merging cannot.
 */
export interface IdentityElsewhereHit {
  readonly objectId: string;
  readonly recordId: string;
  readonly label: string;
  readonly namespaceId: string;
  readonly namespaceLabel: string;
  readonly valueNorm: string;
  readonly isCurrent: boolean;
}

export interface IdentityPanel {
  readonly objectId: string;
  readonly recordId: string;
  readonly keys: readonly IdentityKeyEntry[];
  readonly elsewhere: readonly IdentityElsewhereHit[];
}

/** Why two records are candidates at all: one shared value in one namespace. */
export interface DuplicateEvidence {
  readonly namespaceId: string;
  readonly namespaceLabel: string;
  readonly valueNorm: string;
  readonly isCurrent: boolean;
}

/**
 * A pair that is REAL EVIDENCE but did not reach the approval queue. Every one of them
 * names the guard that demoted it (2.2), because "possible" with no reason is a list
 * nobody trusts and therefore nobody works.
 */
export type DuplicateGuard = 'none' | 'G1' | 'G2' | 'G2b' | 'G3' | 'G4';

export interface PossibleDuplicate {
  readonly objectId: string;
  readonly recordId: string;
  readonly otherRecordId: string;
  readonly evidence: readonly DuplicateEvidence[];
  readonly guard: DuplicateGuard;
  /** The demotion in one sentence a non-technical reviewer can judge. */
  readonly reason: string;
}

/** 4.2's ladder, per field, as the review screen needs it. */
export interface MergeFieldDecision {
  readonly fieldId: string;
  readonly source: 'winner' | 'loser';
  readonly value: FieldValue;
  readonly winnerValue: FieldValue;
  readonly loserValue: FieldValue;
  readonly contested: boolean;
  readonly identical: boolean;
  readonly reason: 'identical' | 'human_lock' | 'non_empty' | 'winner_default';
}

/** 4.4: one (object, field) whose rows point at the loser and will be repointed. */
export interface MergeRepoint {
  readonly objectId: string;
  readonly objectLabel: string;
  readonly fieldId: string;
  readonly fieldLabel: string;
  readonly count: number;
}

export interface MergePreflight {
  readonly objectId: string;
  readonly winnerRecordId: string;
  readonly loserRecordId: string;
  readonly decisions: readonly MergeFieldDecision[];
  /** Surviving values that DIFFER from what the winner holds today (section 3). */
  readonly payload: Readonly<Record<string, FieldValue>>;
  readonly contestedFieldIds: readonly string[];
  readonly repoints: readonly MergeRepoint[];
  readonly totalRepointed: number;
  /** Set when the merge REFUSES: above the declared transaction bound, or a record is gone. */
  readonly refusal: string | null;
  /** False journal completeness: above the cap the merge cannot be fully undone (4.7). */
  readonly unmergePartial: boolean;
  readonly fingerprint: string;
}

/** One applied merge, as the record's merge history shows it. */
export interface MergeRecord {
  readonly id: string;
  readonly objectId: string;
  readonly winnerRecordId: string;
  readonly loserRecordId: string;
  readonly decidedBy: Actor;
  readonly decidedAt: string;
  readonly unmergePartial: boolean;
  readonly undoneAt: string | null;
}

/**
 * 4.7: the preflight's HONESTY is the point. Unmerge is a repair, not time travel, so
 * what it cannot put back is named before the click rather than discovered afterwards.
 */
export interface UnmergePreflight {
  readonly mergeId: string;
  readonly objectId: string;
  readonly winnerRecordId: string;
  readonly loserRecordId: string;
  readonly revertableFieldIds: readonly string[];
  /** Written after the merge, so nothing can know which human they belong to. */
  readonly changedSinceFieldIds: readonly string[];
  readonly strandedRelations: readonly {
    readonly objectLabel: string;
    readonly fieldLabel: string;
    readonly count: number;
  }[];
  /** Live collisions on unique-and-identifier fields; each one REFUSES the unmerge. */
  readonly collisions: readonly { readonly fieldLabel: string; readonly value: string }[];
  readonly unmergePartial: boolean;
  readonly refusal: string | null;
  /** INV-8: the second call carries this back. */
  readonly confirmToken: string;
}

export interface UnmergeResult {
  readonly mergeId: string;
  readonly winnerRecordId: string;
  readonly loserRecordId: string;
  readonly revertedFieldIds: readonly string[];
  readonly keptFieldIds: readonly string[];
  readonly repointedBack: number;
  readonly strandedRelations: UnmergePreflight['strandedRelations'];
}

/**
 * Section 3's first operator control: it stops the detector CREATING proposals without
 * stopping key writes, so a bad namespace is contained in one click while it is fixed
 * instead of being fixed under a growing queue.
 */
export interface DetectorState {
  readonly paused: boolean;
}

export interface IdentityClient {
  listNamespaces(): Promise<readonly IdentityNamespace[]>;
  createNamespace(input: CreateNamespaceInput): Promise<IdentityNamespace>;
  updateNamespace(namespaceId: string, input: UpdateNamespaceInput): Promise<IdentityNamespace>;
  /** Refuses while a live field still points at it, with a message naming those fields. */
  deleteNamespace(namespaceId: string): Promise<void>;

  /** This record's own keys plus the cross-object hits on the same identifiers (2.6). */
  getRecordIdentity(objectId: string, recordId: string): Promise<IdentityPanel>;
  recordDuplicates(objectId: string, recordId: string): Promise<readonly PossibleDuplicate[]>;
  objectDuplicates(objectId: string): Promise<readonly PossibleDuplicate[]>;

  mergePreflight(input: {
    readonly objectId: string;
    readonly winnerRecordId: string;
    readonly loserRecordId: string;
  }): Promise<MergePreflight>;
  /** The winner is the server's call (4.1, oldest wins); the caller names an unordered pair. */
  proposeMerge(input: {
    readonly objectId: string;
    readonly recordA: string;
    readonly recordB: string;
  }): Promise<PendingChangeSummary>;
  listMerges(filter: {
    readonly recordId?: string;
    readonly objectId?: string;
  }): Promise<readonly MergeRecord[]>;

  unmergePreflight(mergeId: string): Promise<UnmergePreflight>;
  /** Destructive (INV-8): without a token the server refuses and hands one back. */
  unmerge(mergeId: string, confirmToken?: string): Promise<UnmergeResult>;

  getDetector(): Promise<DetectorState>;
  setDetector(paused: boolean): Promise<DetectorState>;
}

/**
 * OUTBOUND WEBHOOKS (docs/decisions/webhooks.md section 7).
 *
 * The wire types come from `@ofmos/shared/webhook` unmodified rather than being restated
 * here: the envelope and the subscription shape are a CONTRACT with external consumers,
 * and a UI-local copy of a contract is a copy that drifts.
 */
export interface WebhookClient {
  list(): Promise<{
    readonly subscriptions: readonly WebhookSubscriptionSummary[];
    readonly health: Readonly<Record<string, WebhookSubscriptionHealth>>;
  }>;
  /** `reveal` returns the signing secret and needs a browser session, never a key. */
  get(id: string, reveal?: boolean): Promise<WebhookSubscriptionSummary>;
  create(input: CreateWebhookSubscriptionRequest): Promise<WebhookSubscriptionSummary>;
  update(id: string, input: UpdateWebhookSubscriptionRequest): Promise<WebhookSubscriptionSummary>;
  remove(id: string): Promise<void>;
  rotateSecret(id: string): Promise<WebhookSubscriptionSummary>;
  test(id: string): Promise<WebhookTestResult>;
  deliveries(
    id: string,
    filter?: { readonly status?: WebhookDeliveryStatus; readonly limit?: number },
  ): Promise<readonly WebhookDeliverySummary[]>;
  replay(deliveryId: string, mode: WebhookReplayMode): Promise<WebhookDeliverySummary>;
}

/**
 * NOTES, TASKS AND COMMENTS (docs/decisions/notes-tasks-comments.md section 10).
 *
 * The summary types come from `@ofmos/shared/annotations` unmodified, for the reason the
 * webhook client states above: four consumers agree on that contract (the REST routes, the
 * MCP dispatcher, this client and the OpenAPI document), and a UI-local copy of a contract
 * is a copy that drifts. Nothing here is a FIELD - no field ids, no provenance map, no
 * `values` keyed by uuid - which is exactly why D1 chose dedicated tables over the records
 * engine, and why these calls sit beside `RecordClient` rather than inside it.
 */
export type {
  AnnotationUser,
  CommentSummary,
  MyTaskEntry,
  MyTaskParent,
  MyTasksPage,
  NoteSummary,
  TaskSummary,
} from '@ofmos/shared/annotations';

/**
 * The my-tasks feed's filter (12.2's Mine | Everyone segmented control).
 *
 * `assignee` is `me`, `everyone` or a user id and is spelled out rather than implied by
 * the session: "whose tasks am I looking at" is the question the route answers, and a
 * default that changes meaning with the caller is a cache entry nobody can share.
 */
export interface MyTasksFilter {
  readonly assignee: string;
  /** Open only. False is the archive view; the wire spells it `true` / `false`. */
  readonly open: boolean;
  readonly limit?: number;
  readonly cursor?: string;
  /**
   * `YYYY-MM-DD`: due that day or earlier (dashboard.md D11). The dashboard's Open items
   * panel is this feed with a window, and it says so in its own title - `due_date` is
   * nullable, so the window silently excludes the undated backlog.
   */
  readonly dueTo?: string;
  /** One kind only (`open_item` / `follow_up`, migration 0027); absent is both. */
  readonly kind?: TaskKind;
}

/**
 * The COUNT's filter: the same two parameters, minus the page (D15).
 *
 * Its own type rather than `Pick<MyTasksFilter, ...>`, because it is also the cache key
 * (`queryKeys.myTaskCount`) and a key whose shape is derived from a bigger type gains a
 * field the day that type does - silently splitting every entry in the cache.
 */
export interface MyTaskCountFilter {
  readonly assignee: string;
  readonly dueTo?: string;
  readonly kind?: TaskKind;
}

/**
 * A comment AS THE THREAD HOLDS IT: the server's summary, plus a client-only flag for a
 * row that exists optimistically and does not exist on the server yet.
 *
 * The flag is not cosmetic. An optimistic comment carries a CLIENT-MINTED id, so replying
 * to it or deleting it addresses a row the server has never heard of - a 404 for the
 * person who typed fastest. The surfaces disable those actions while it is set, which is
 * also what makes the flow deterministic for a test driving it at machine speed.
 */
export type ThreadComment = CommentSummary & { readonly pending?: boolean | undefined };

export interface AnnotationClient {
  listNotes(objectId: string, recordId: string): Promise<readonly NoteSummary[]>;
  createNote(objectId: string, recordId: string, body: string): Promise<NoteSummary>;
  /**
   * `ifUnmodifiedSince` is the row's `updated_at` as the caller last read it. Optional in
   * the contract and mandatory in practice for an editor: without it two humans editing
   * one note silently overwrite each other, and with it the server answers 409 (2.6).
   */
  updateNote(noteId: string, body: string, ifUnmodifiedSince?: string): Promise<NoteSummary>;
  deleteNote(noteId: string): Promise<void>;

  listTasks(objectId: string, recordId: string): Promise<readonly TaskSummary[]>;
  createTask(objectId: string, recordId: string, input: CreateTaskRequest): Promise<TaskSummary>;
  /**
   * Every key optional, `null` clears and absent leaves alone - the record write path's
   * distinction. No `ifUnmodifiedSince`: a task's fields are last-write-wins coordination
   * state, and `done` maps to the guarded complete/reopen statements rather than a column.
   */
  updateTask(taskId: string, patch: UpdateTaskRequest): Promise<TaskSummary>;
  deleteTask(taskId: string): Promise<void>;

  listMyTasks(query: MyTasksFilter): Promise<MyTasksPage>;
  /**
   * How many, over the SAME set `listMyTasks` would page through.
   *
   * No argument is the tab-bar badge: own open tasks, whenever they are due. With a
   * filter it is a dashboard tile, which is its panel's request with a count instead of
   * a page - and the server enforces D9 on the `everyone` scope, because a number over a
   * book the caller cannot read discloses its size.
   */
  countMyOpenTasks(filter?: MyTaskCountFilter): Promise<number>;

  /**
   * The record's comments, note threads INCLUDED. One list rather than one per note (3.5):
   * grouping by `noteId` is a client-side partition of data already in hand, and a query
   * per note card would issue a request per scroll.
   */
  listComments(
    objectId: string,
    recordId: string,
    noteId?: string,
  ): Promise<readonly CommentSummary[]>;
  createComment(
    objectId: string,
    recordId: string,
    input: CreateCommentRequest,
  ): Promise<CommentSummary>;
  updateComment(
    commentId: string,
    body: string,
    ifUnmodifiedSince?: string,
  ): Promise<CommentSummary>;
  deleteComment(commentId: string): Promise<void>;

  /** The assignee picker's data. Deactivated users are RETURNED, marked, never hidden. */
  listUsers(): Promise<readonly AnnotationUser[]>;
}

/**
 * SAVED VIEWS (record-page-and-views.md D14-D19).
 *
 * The wire types come from `@ofmos/shared/view` unmodified, for the reason the webhook
 * and annotation clients give: a UI-local copy of a contract is a copy that drifts. A
 * view is per OBJECT, shared with everybody (D14, INV-15) and carries a
 * `ViewFilterNode` - the ordinary filter AST plus D17's relative-date operand, which is
 * materialised in the BROWSER before anything queries, because the person reading the
 * view is the person whose week it is.
 */
/** D27's wire shape, re-exported so screens import one module (D21's editor row). */
export type {
  FilterAnchor,
  RelationCountComparator,
  RelationDirection,
  RelationQuantifier,
} from '@ofmos/shared/filter-ast';

export type {
  RelativeDateToken,
  SavedView,
  SavedViewMode,
  SavedViewPatchInput,
  SavedViewWriteInput,
  ViewFilterNode,
} from '@ofmos/shared/view';

/**
 * `ReportClient` over `/api/reports` (`docs/decisions/reports.md` T5 to T7).
 *
 * The RUN is the half that makes this different from `ViewClient`: a report is a saved
 * QUESTION, so reading one gives you a definition and running it gives you numbers, and
 * the numbers are bounded by the caller's own field and record access. Two people can
 * legitimately get different answers from the same report.
 */
export interface ReportClient {
  list(): Promise<readonly Report[]>;
  get(reportId: string): Promise<Report>;
  create(input: ReportWriteInput): Promise<Report>;
  update(reportId: string, patch: ReportPatchInput): Promise<Report>;
  /** Soft: it frees the name. There is no hard delete. */
  remove(reportId: string): Promise<void>;
  /**
   * `range` overrides the stored one for THIS read and saves nothing - changing the window
   * is a different question about the same report, not an edit to it. `timeZone` is the
   * browser's, sent on every grouped read (D13): a bucket key is a wall-clock date and the
   * server has no opinion about whose wall it is on.
   */
  run(reportId: string, options?: RunReportRequest): Promise<ReportRunResult>;
}

export interface RunReportRequest {
  readonly range?: ReportRange | undefined;
  readonly timeZone?: string | undefined;
}

export interface ReportRunResult {
  readonly report: Report;
  readonly result: RecordGroupSet;
}

export interface ViewClient {
  /** Every view, or every view of ONE object. */
  list(objectId?: string): Promise<readonly SavedView[]>;
  create(input: SavedViewWriteInput): Promise<SavedView>;
  /**
   * `objectId` is deliberately not patchable: moving a view to another object would
   * invalidate its columns, its filter, its sort and its board group field at once.
   * That is a new view, not an edit.
   */
  update(viewId: string, patch: SavedViewPatchInput): Promise<SavedView>;
  /** Soft: it frees the name and the default slot. There is no hard delete. */
  remove(viewId: string): Promise<void>;
}

/**
 * WORKFLOWS (docs/decisions/workflow-builder.md D45).
 *
 * The wire types come from `@ofmos/shared/workflow` unmodified, for the reason the webhook
 * client gives: the trigger, the step union and the run view are a CONTRACT the server
 * validates against, and a UI-local copy of a contract is a copy that drifts. The sentence
 * renderers come from there too, so the list row and the run detail read the same words the
 * server would.
 */
export interface WorkflowClient {
  list(): Promise<readonly WorkflowSummary[]>;
  get(id: string): Promise<WorkflowDetail>;
  create(input: CreateWorkflowInput): Promise<WorkflowDetail>;
  /** Name, description and the per-workflow switch. Switching on also clears a pause. */
  update(id: string, patch: PatchWorkflowInput): Promise<WorkflowDetail>;
  /** Soft: its runs stay readable, and its schedule is unregistered with it (D33). */
  remove(id: string): Promise<void>;
  /** THE WHOLE DRAFT. A step list is a document; partial updates need a merge policy. */
  saveDraft(id: string, draft: WorkflowDraftInput): Promise<WorkflowDetail>;
  /** Refuses for a schedule trigger when the server has no job system attached (C3). */
  activate(id: string): Promise<WorkflowDetail>;
  /** D12: on a record the caller picks. Refuses on the brake, on a pause, on no match. */
  run(id: string, recordId: string): Promise<WorkflowRunView>;
  runs(
    id: string,
    filter?: { readonly segment?: WorkflowRunSegment; readonly limit?: number },
  ): Promise<{ readonly runs: readonly WorkflowRunView[]; readonly nextCursor: string | null }>;
  /** Schedule triggers only; empty for the other two, which is what the branch reads. */
  fires(
    id: string,
    filter?: { readonly limit?: number },
  ): Promise<{
    readonly fires: readonly WorkflowScheduleFireView[];
    readonly nextCursor: string | null;
  }>;
  getRun(runId: string): Promise<WorkflowRunDetail>;
  /** A NEW run from the beginning, on the same version and record (D35). */
  rerun(runId: string): Promise<WorkflowRunView>;
  settings(): Promise<WorkflowSettings>;
  setSettings(paused: boolean): Promise<WorkflowSettings>;
}

/**
 * CSV IMPORT AND EXPORT (docs/decisions/csv-import-export.md).
 *
 * The VOCABULARY comes from `@ofmos/shared/csv` unmodified - statuses, modes, outcomes,
 * the caps, the per-column format schema - for the reason the workflow client gives: those
 * are a contract the server validates against, and a UI-local copy of a contract drifts.
 *
 * The RUN ROWS are declared here rather than in `shared`, and the difference is real
 * rather than an oversight. `meta.import_run` and `meta.export_run` are read on the server
 * as Kysely rows with `Date` columns; what crosses the wire is their JSON, where every one
 * of those is an ISO string. `RecordRow` is declared here for exactly that reason, and
 * these two follow it. Every field below is the server row's field under the same name.
 */
export type {
  CsvDelimiter,
  ExportRunStatus,
  ImportCellOutcome,
  ImportMatchKeySource,
  ImportMode,
  ImportRowErrorCode,
  ImportRowOutcome,
  ImportRunKind,
  ImportRunStatus,
  ImportValueFix,
} from '@ofmos/shared/csv';
/** The per-column declaration lives beside `valueSchema` in the registry, not in `csv` (D7, D8). */
export type { ColumnFormat } from '@ofmos/shared/field-types';

export interface ImportRunView {
  readonly id: string;
  readonly kind: ImportRunKind;
  /** The import a revert undoes. Null on an import run (D44). */
  readonly revertOfRunId: string | null;
  readonly objectMetadataId: string;
  readonly createdBy: string | null;
  readonly actorContext: string | null;
  readonly filename: string | null;
  readonly delimiter: string | null;
  readonly status: ImportRunStatus;
  readonly mode: ImportMode | null;
  readonly matchKeyFieldId: string | null;
  /** Column index (as a string key) to target field id, or null for unmapped (D6). */
  readonly mapping: Readonly<Record<string, string | null>>;
  readonly formats: Readonly<Record<string, ColumnFormat>>;
  readonly valueFixes: Readonly<Record<string, readonly ImportValueFix[]>>;
  readonly rowCount: number | null;
  readonly createdCount: number;
  readonly updatedCount: number;
  readonly failedCount: number;
  readonly blockedValues: number;
  readonly invalidValues: number;
  readonly revertedCount: number;
  readonly skippedTouchedCount: number;
  readonly skippedUpdatedCount: number;
  /**
   * D47's second pass: links that now exist, keys that named no record or several, and links
   * a human's correction refused on the record they were pointing at (INV-5).
   */
  readonly relationsLinked: number;
  readonly relationsUnresolved: number;
  readonly relationsBlocked: number;
  /** D45: null on every run the wizard made, which is almost all of them. */
  readonly batchId: string | null;
  readonly batchPosition: number | null;
  /**
   * D25 as amended: which of the two date columns this run's mapping targets. Derived by the
   * route from metadata rather than stored, so the run screen can say what happened to an
   * updated record's dates long after the wizard's state is gone.
   */
  readonly timestampColumns: { readonly createdAt: boolean; readonly updatedAt: boolean };
  /** How far the run got, which is what a Retry resumes from (D41). */
  readonly lastCommittedRow: number;
  readonly errorReason: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
}

export interface ExportRunView {
  readonly id: string;
  readonly createdBy: string | null;
  readonly objectMetadataId: string;
  /** The stored request envelope: the `RecordQuery` plus `recordIds` and `allFields`. */
  readonly query: unknown;
  readonly status: ExportRunStatus;
  readonly rowCount: number | null;
  readonly byteCount: number | null;
  /** D35's honesty line: a point-in-time note naming a mutable sort column. */
  readonly note: string | null;
  readonly errorReason: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly firstDownloadedAt: string | null;
  readonly downloadCount: number;
  readonly expiresAt: string | null;
  /** False once the 7-day sweep has nulled the bytes: the row survives as the audit (D34). */
  readonly hasFile: boolean;
}

/** One file column, as the mapping screen renders it (D6, D7). */
export interface ImportWizardColumn {
  readonly columnIndex: number;
  readonly header: string;
  /** Non-empty values in file order; the screen shows the first three. */
  readonly samples: readonly string[];
  readonly autoMatch: 'matched' | 'ambiguous' | 'unmapped';
  /** Populated when `ambiguous`: a tie maps NOTHING, and these are what tied (D6). */
  readonly candidateFieldIds: readonly string[];
}

/** One field a column may target, with what the screen needs to decide about it. */
export interface ImportWizardTarget {
  readonly fieldId: string;
  readonly name: string;
  readonly label: string;
  /** `assignee` is D25's one writable system field; `recordId` is addressable, never written. */
  readonly role: 'field' | 'assignee' | 'recordId';
  readonly fieldType: string;
  /** Null when this field cannot be the run's match key. D14 has three sources. */
  readonly matchKeySource: ImportMatchKeySource | null;
}

export interface ImportWizardState {
  readonly headers: readonly string[];
  readonly columns: readonly ImportWizardColumn[];
  readonly targets: readonly ImportWizardTarget[];
}

/** D11's aggregation for one column. `aggregated: false` means the cap was passed. */
export interface ImportColumnReview {
  readonly columnIndex: number;
  readonly aggregated: boolean;
  readonly values: readonly { readonly value: string; readonly rows: number }[];
}

/** D26: what this file would set off, per workflow, and whether the breaker stops it. */
export interface ImportFanOutWorkflowView {
  readonly workflowId: string;
  readonly workflowName: string;
  readonly actions: readonly string[];
  readonly runsRemainingThisHour: number;
  readonly rowsMatchingTrigger: number;
  readonly willBePaused: boolean;
}

export interface ImportPreviewView {
  readonly willCreate: number;
  readonly willUpdate: number;
  readonly unchanged: number;
  readonly willFail: number;
  readonly failuresByCode: Readonly<Record<string, number>>;
  readonly invalidValues: number;
  readonly blockedValues: number;
  readonly unmappedColumns: readonly string[];
  readonly existingRecords: number;
  readonly fanOut: { readonly workflows: readonly ImportFanOutWorkflowView[] };
  /** D40: an identifier backfill in flight, and whether it blocks this run outright. */
  readonly backfill: {
    readonly pendingFieldLabels: readonly string[];
    readonly blocksRun: boolean;
  };
  /**
   * D47: how many keys the second pass will try, and which columns carry them. An UPPER
   * BOUND: a key is not known to name nothing until the pass has read the other object,
   * which happens after the records exist.
   */
  readonly links: {
    readonly columnLabels: readonly string[];
    readonly keys: number;
  };
  /** D25 as amended: which of the two date columns this file maps. */
  readonly timestamps: { readonly createdAt: boolean; readonly updatedAt: boolean };
  /**
   * D48: whether this run can detect a collision with a deleted record at all. False in
   * `create` mode against an object with no identifier field is the honest limit that
   * survives the probe.
   */
  readonly deletedProbe: {
    readonly active: boolean;
    readonly fieldLabels: readonly string[];
    /**
     * Every identifier field the OBJECT carries. Empty with `active: false` means the object
     * has none (D48's surviving limit); non-empty means this file does not carry one, which
     * is a different sentence with a different remedy.
     */
    readonly objectIdentifierLabels: readonly string[];
  };
}

/** D44's three numbers, read BEFORE the operator commits the revert. */
export interface RevertPreviewView {
  readonly willRevert: number;
  readonly skippedTouched: number;
  readonly skippedUpdated: number;
}

export interface ImportUploadResult {
  readonly run: ImportRunView;
  readonly headers: readonly string[];
  readonly delimiter: CsvDelimiter;
  readonly rowCount: number;
}

export interface ImportConfigPatch {
  readonly mode?: ImportMode | undefined;
  readonly matchKeyFieldId?: string | null | undefined;
  readonly columns?:
    | readonly {
        readonly columnIndex: number;
        readonly targetFieldId: string | null;
        readonly format?: ColumnFormat | undefined;
      }[]
    | undefined;
}

export interface CreateExportInput {
  readonly query: RecordQuery;
  /** A selection NARROWS the export, capped at `EXPORT_SELECTION_MAX` (D32). */
  readonly recordIds?: readonly string[] | undefined;
  /** "All fields on this object", a second explicit choice defaulting off (D32). */
  readonly allFields?: boolean | undefined;
}

export interface CsvClient {
  /** D1: the draft names an object; the file follows on its own route. */
  createImport(objectMetadataId: string): Promise<ImportRunView>;
  /** D3: the raw bytes as `text/csv`. Every cap is a named refusal here (D5). */
  uploadImportFile(
    runId: string,
    file: { readonly filename: string; readonly text: string; readonly delimiter?: CsvDelimiter },
  ): Promise<ImportUploadResult>;
  listImports(limit?: number): Promise<readonly ImportRunView[]>;
  getImport(runId: string): Promise<ImportRunView>;
  /** The mapping screen's read: headers, samples, ambiguity and the targets. */
  getImportWizard(runId: string): Promise<{
    readonly run: ImportRunView;
    readonly wizard: ImportWizardState;
  }>;
  /** D43: configuration only. The review edits travel on their own route. */
  updateImport(runId: string, patch: ImportConfigPatch): Promise<ImportRunView>;
  reviewColumn(runId: string, columnIndex: number): Promise<ImportColumnReview>;
  /** D43: ONE column per request, bounded by D11's cap rather than by the file. */
  setValueFixes(
    runId: string,
    columnIndex: number,
    fixes: readonly ImportValueFix[],
  ): Promise<void>;
  /** D21: the preview IS the approval, and it moves the run to `ready`. */
  previewImport(runId: string): Promise<{
    readonly run: ImportRunView;
    readonly preview: ImportPreviewView;
  }>;
  startImport(runId: string): Promise<ImportRunView>;
  retryImport(runId: string): Promise<ImportRunView>;
  cancelImport(runId: string): Promise<ImportRunView>;
  removeImport(runId: string): Promise<void>;
  /** D44's confirm-dialog numbers. Only a completed import has them. */
  revertPreview(runId: string): Promise<RevertPreviewView>;
  /** D44: a NEW run of kind `revert`, born queued on the object. */
  revertImport(runId: string): Promise<ImportRunView>;
  /** D13: the failed rows in their ORIGINAL shape, as CSV text. */
  importErrorsCsv(runId: string): Promise<string>;
  /** D44: every row the revert considered, in all three outcomes. Revert runs only. */
  revertReportCsv(runId: string): Promise<string>;

  /** D32: the scope is the query the screen is showing. 202, then the run is polled. */
  createExport(input: CreateExportInput): Promise<ExportRunView>;
  listExports(limit?: number): Promise<readonly ExportRunView[]>;
  getExport(runId: string): Promise<ExportRunView>;
  retryExport(runId: string): Promise<ExportRunView>;
  /** D34: the stored file, as CSV text. 400 once it is expired or not yet complete. */
  exportFileCsv(runId: string): Promise<string>;
}

/**
 * THE BYTE CLIENT (`docs/decisions/file-attachments.md` D3, D5, D19).
 *
 * FOUR MEMBERS, and they are the whole browser-side surface of the module: reserve a row,
 * PUT the bytes, GET them back, and the URL an `<img>` may point at.
 *
 * There is NO cache and no query key here, and that is not an INV-13 loophole. INV-13
 * governs record DATA, and an attachment's data - id, label, size, type - lives in the
 * record under `['record', objectId, recordId]` like every other field value. The BYTES are
 * not data in that sense: they reach the DOM as an `<img src>` the browser caches, or as a
 * Blob the download path revokes immediately. Putting a 25 MB buffer in a TanStack cache
 * would BE the loophole, and obligation 49 asserts that no query function returns one.
 */
export interface FilesClient {
  /** D3: the ledger row, before a single byte exists. Refuses 0 and anything over the cap. */
  reserve(input: {
    readonly label: string;
    readonly byteCount: number;
  }): Promise<{ readonly fileId: string; readonly expiresAt: string | null }>;
  /** D3: the bytes, as one raw `application/octet-stream` body through the ONE transport. */
  upload(fileId: string, bytes: Blob): Promise<RecordFileItem>;
  /** D5: the bytes back, as a Blob the screen turns into a file. */
  download(fileId: string): Promise<Blob>;
  /**
   * D5, D19: what an `<img src>` may point at.
   *
   * A METHOD rather than a template string at the call site, because the fixtures build has
   * no server behind that path and has to answer with the inline data URL D19 seeds instead.
   * A component that built the path itself would render a broken image in the review build
   * and nobody would know until a screenshot round.
   */
  previewUrl(fileId: string): string;
}

/**
 * INBOUND INTAKE (`docs/decisions/inbound-intake.md` D19), the fourteenth surface.
 *
 * The wire types come from `@ofmos/shared/intake` unmodified, for the reason the webhook
 * and workflow clients give: a source document is a CONTRACT the server validates against
 * and then executes on a background worker, and a UI-local copy of a contract is a copy
 * that drifts. The settings screen edits exactly what `intakeSourceWriteSchema` accepts.
 *
 * NOTHING HERE EVER CARRIES A CREDENTIAL (P4, D13). `setCredential` sends one and gets a
 * DATE back; no method returns one, because no route does. The minted key is the single
 * exception and it is a one-time response, never a cache entry - see `useMintIntakeKey`.
 */
export interface IntakeClient {
  /**
   * The sources AND whether the module's own delivery object is there (A2). One request,
   * because the board that renders the rows is the board that has to say when the door
   * itself is missing.
   */
  list(): Promise<IntakeSourceList>;
  get(sourceId: string): Promise<IntakeSource>;
  create(input: IntakeSourceWriteInput): Promise<IntakeSource>;
  /**
   * The two documents are REPLACED WHOLE when they are sent, and `key` is absent from the
   * patch input on purpose: it is what an external system posts to and half of every
   * stored external id, so renaming it would orphan every delivery already received.
   */
  patch(sourceId: string, patch: IntakeSourcePatchInput): Promise<IntakeSource>;
  /** Soft: it frees the key and clears the credential. */
  remove(sourceId: string): Promise<void>;
  /** Write-only (P4). The answer is when it was set, never what it is. */
  setCredential(sourceId: string, secret: string): Promise<string>;
  /**
   * A REPORT, NOT A REFUSAL: the route answers 200 with `valid: false` and the issues.
   * Sending a candidate asks "is what I am about to save legal"; sending nothing asks "is
   * what I saved months ago still legal", which is the question that outlives the first.
   */
  validate(sourceId: string, candidate?: IntakeValidateInput): Promise<IntakeValidationResult>;
  /** D15: the plaintext is in this response and nowhere else, ever. A second mint replaces. */
  mintKey(sourceId: string): Promise<IntakeSourceKeyMinted>;
  /** The mapper's own walk, stopped before the write. Nothing is stored. */
  dryRun(sourceId: string, payload: unknown): Promise<IntakeDryRunResult>;
  /**
   * A2: the AI's proposal for one example delivery - a target and a rule per path, with a
   * line of reasoning each. NOTHING IS STORED: the operator edits the rows and saves through
   * `patch`, which is what keeps a bad suggestion at the cost of a click.
   */
  suggestMapping(sourceId: string, sample: unknown): Promise<IntakeMappingSuggestionResult>;
  /** Fetches the listing now, and RESUMES a pull the last failure paused (S2b entry 8). */
  pullNow(sourceId: string): Promise<IntakePullNow>;
  status(sourceId: string): Promise<IntakeSourceStatusReport>;
  /** D11's operator retry, on one stored delivery. */
  applySubmission(recordId: string): Promise<IntakeApplyResult>;
}

/** What `POST /:id/validate` may be asked about: the stored document, or a candidate. */
export interface IntakeValidateInput {
  readonly submissionBinding?: IntakeSubmissionBinding | undefined;
  readonly mapping?: IntakeMapping | undefined;
  readonly pull?: IntakePullConfig | null | undefined;
}

/**
 * WHAT "PULL NOW" ANSWERS, and it is two shapes because the server has two answers: a run
 * that happened here (no worker attached) reports its counts, and a queued one reports that
 * it is queued. The screen says a different sentence for each rather than inventing counts
 * for a run that has not started.
 */
export type IntakePullNow =
  | { readonly queued: true }
  | {
      /** Absent on this arm, which is what makes `queued === true` a real narrowing. */
      readonly queued?: undefined;
      readonly listed: number;
      readonly skipped: number;
      readonly received: number;
      readonly refused: number;
      readonly truncated: boolean;
      readonly error: string | null;
    };

export interface IntakeApplyResult {
  readonly submissionId: string;
  readonly status: string;
}

/**
 * THE HEALTH RULES (`docs/decisions/phase-b-coach.md` D12), as the browser sees them.
 *
 * A row rather than a bare document, because the Settings page needs the id to PATCH and
 * the record page needs to know WHICH object a rule set belongs to before it can decide
 * that the group it is rendering is the computed health block (D17).
 */
export interface HealthConfigRecord {
  readonly id: string;
  readonly universalId: string;
  readonly origin: MetadataOrigin;
  readonly objectMetadataId: string;
  readonly config: HealthConfig;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** What a pass reported. The Settings page shows it in a toast and stores nothing. */
export interface HealthRunSummary {
  readonly scored: number;
  readonly changed: number;
  readonly tasksCreated: number;
  /**
   * The two counts that say a pass did LESS than it looks: writes a human lock refused, and
   * records it stepped over after a throw. The toast names them when either is non-zero,
   * because this is the one moment somebody is watching the engine work.
   */
  readonly blocked: number;
  readonly failed: number;
  /** Rule sets another pass already held, so this one skipped them. */
  readonly skipped: readonly string[];
}

/**
 * READING IS OPEN, WRITING IS NOT, which is the split the routes make: the record page has
 * to read the rules to find its own computed block, and only `HEALTH_MANAGE` may change what
 * red means. There is no create and no delete: one object holds one rule set, the seed
 * creates it, and the Settings page edits it.
 */
export interface HealthClient {
  list(): Promise<readonly HealthConfigRecord[]>;
  /** The WHOLE document, never a merge: the fields and the thresholds constrain each other. */
  update(id: string, config: HealthConfig): Promise<HealthConfigRecord>;
  /** Scores every record of the object now and says what moved. */
  run(id: string): Promise<HealthRunSummary>;
}

/**
 * HOW OLD EACH DATA SOURCE IS (redesign D9): Telegram read, calls synced, intakes checked,
 * readable by every signed-in person (`GET /api/feeds/status`).
 */
export interface FeedsClient {
  status(): Promise<FeedsStatus>;
  /**
   * "Retry now" (redesign D10): `POST /api/feeds/touch/run`, `HEALTH_MANAGE` only. The answer
   * says the run was QUEUED and nothing more (record 10), so the page re-reads `status()`
   * afterwards rather than claiming the run happened.
   */
  retryTouch(): Promise<TouchFeedRetry>;
}

/**
 * THE FULFILLMENT PAGES' OWN WRITES. "Mark as handled" (redesign D2) stamps the SERVER'S time
 * on the agency (`POST /api/fulfillment/agencies/:agencyId/reply-handled`), never the
 * browser's; `clearReplyHandled` empties it again, which is what the undo toast calls. Both
 * answer the stored value.
 */
export interface FulfillmentClient {
  markReplyHandled(agencyId: string): Promise<ReplyHandled>;
  clearReplyHandled(agencyId: string): Promise<ReplyHandled>;
  /**
   * "MARK AS REVIEWED" ON AN INTAKE (redesign D10; ledger BE-INR):
   * `POST /api/intake/submissions/:submissionId/reviewed` stamps the SERVER'S time on the
   * delivery's "Reviewed at", and `clearIntakeReviewed` (`DELETE`, the undo) empties it again.
   * Both answer the stored value; who pressed it is the actor of the write, on the timeline.
   */
  markIntakeReviewed(submissionId: string): Promise<IntakeReviewed>;
  clearIntakeReviewed(submissionId: string): Promise<IntakeReviewed>;
  /**
   * THE COLOUR HISTORY (redesign D10, record 11): `GET /api/fulfillment/health-history`, one
   * entry per day from `since` to today over today's active clients, and the clients whose
   * colour differs between `since` and today. What the Dashboard's tile deltas, sparklines and
   * "+N since last week" names read.
   */
  healthHistory(query: HealthHistoryQuery): Promise<HealthHistoryResponse>;
}

export interface AppClient {
  readonly metadata: MetadataClient;
  readonly records: RecordClient;
  readonly auth: AuthClient;
  readonly admin: AdminClient;
  readonly pending: PendingClient;
  readonly identity: IdentityClient;
  readonly webhooks: WebhookClient;
  readonly annotations: AnnotationClient;
  readonly reports: ReportClient;
  readonly views: ViewClient;
  readonly workflows: WorkflowClient;
  readonly csv: CsvClient;
  readonly files: FilesClient;
  readonly intake: IntakeClient;
  readonly health: HealthClient;
  readonly feeds: FeedsClient;
  readonly fulfillment: FulfillmentClient;
}
